import React from 'react';
import { motion } from 'framer-motion';
export function AboutNAD() {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} transition={{
    duration: 0.6,
    ease: 'easeOut'
  }} className="w-full min-h-screen bg-white" data-id="element-1">
      {/* Hero Section */}
      <section className="px-6 py-32 md:py-40 bg-zinc-50 text-zinc-950" data-id="element-2">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3">
          <motion.p className="text-sm tracking-[0.3em] uppercase mb-8 text-zinc-600" initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-4">
            The Science
          </motion.p>
          <motion.h1 className="text-5xl md:text-7xl font-serif mb-8 leading-tight" style={{
          fontFamily: 'Playfair Display, serif'
        }} initial={{
          y: 30,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-5">
            About NAD+
          </motion.h1>
          <motion.p className="text-lg md:text-xl text-zinc-600 leading-relaxed max-w-2xl mx-auto" initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-6">
            Understanding the molecule that powers cellular vitality and
            longevity.
          </motion.p>
        </div>
      </section>

      {/* What is NAD+ */}
      <section className="px-6 py-32 md:py-40 bg-white" data-id="element-7">
        <div className="max-w-7xl mx-auto" data-id="element-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-9">
            <div data-id="element-10">
              <p className="text-sm tracking-[0.3em] uppercase mb-6 text-zinc-600" data-id="element-11">
                The Molecule
              </p>
              <h2 className="text-4xl md:text-5xl font-serif mb-6 text-zinc-950" style={{
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-12">
                What is NAD+?
              </h2>
              <p className="text-lg text-zinc-600 leading-relaxed mb-6" data-id="element-13">
                NAD+ (Nicotinamide Adenine Dinucleotide) is a coenzyme found in
                every cell of your body. It plays a crucial role in energy
                metabolism, DNA repair, and cellular communication.
              </p>
              <p className="text-lg text-zinc-600 leading-relaxed" data-id="element-14">
                As we age, NAD+ levels naturally decline, contributing to
                cellular aging and decreased vitality. Supplementation helps
                restore these critical levels.
              </p>
            </div>
            <div className="aspect-[4/5] bg-zinc-200 overflow-hidden" data-id="element-15">
              <img src="https://images.unsplash.com/photo-1576086213369-97a306d36557?w=800&q=80" alt="Cellular structure" className="w-full h-full object-cover" data-id="element-16" />
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="px-6 py-32 md:py-40 bg-zinc-50" data-id="element-17">
        <div className="max-w-7xl mx-auto" data-id="element-18">
          <div className="text-center mb-20" data-id="element-19">
            <p className="text-sm tracking-[0.3em] uppercase mb-6 text-zinc-600" data-id="element-20">
              Key Benefits
            </p>
            <h2 className="text-4xl md:text-6xl font-serif text-zinc-950" style={{
            fontFamily: 'Playfair Display, serif'
          }} data-id="element-21">
              Why NAD+ Matters
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-22">
            {[{
            title: 'Energy Production',
            description: 'NAD+ is essential for converting nutrients into cellular energy (ATP), powering every function in your body.'
          }, {
            title: 'DNA Repair',
            description: 'Activates sirtuins and PARPs, proteins that repair damaged DNA and maintain genomic stability.'
          }, {
            title: 'Cellular Health',
            description: 'Supports mitochondrial function, reduces oxidative stress, and promotes healthy aging at the cellular level.'
          }].map((benefit, index) => <div key={index} data-id="element-23">
                <h3 className="text-2xl font-serif mb-4 text-zinc-950" style={{
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-24">
                  {benefit.title}
                </h3>
                <p className="text-zinc-600 leading-relaxed" data-id="element-25">
                  {benefit.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Research */}
      <section className="px-6 py-32 md:py-40 bg-white" data-id="element-26">
        <div className="max-w-4xl mx-auto" data-id="element-27">
          <div className="text-center mb-16" data-id="element-28">
            <p className="text-sm tracking-[0.3em] uppercase mb-6 text-zinc-600" data-id="element-29">
              The Research
            </p>
            <h2 className="text-4xl md:text-5xl font-serif mb-8 text-zinc-950" style={{
            fontFamily: 'Playfair Display, serif'
          }} data-id="element-30">
              Backed by Science
            </h2>
            <p className="text-lg text-zinc-600 leading-relaxed" data-id="element-31">
              Decades of research from leading institutions have demonstrated
              NAD+'s critical role in cellular health and longevity. Studies
              show that boosting NAD+ levels can support healthy aging, enhance
              cognitive function, and improve overall vitality.
            </p>
          </div>

          <div className="space-y-8" data-id="element-32">
            {[{
            institution: 'Harvard Medical School',
            finding: 'NAD+ precursors reverse aspects of aging in mice'
          }, {
            institution: 'Washington University',
            finding: 'NAD+ supplementation improves mitochondrial function'
          }, {
            institution: 'Keio University',
            finding: 'NAD+ levels correlate with healthy aging markers'
          }].map((study, index) => <div key={index} className="border-l-2 border-zinc-950 pl-6" data-id="element-33">
                <p className="text-sm tracking-[0.2em] uppercase text-zinc-600 mb-2" data-id="element-34">
                  {study.institution}
                </p>
                <p className="text-lg text-zinc-950" data-id="element-35">{study.finding}</p>
              </div>)}
          </div>
        </div>
      </section>
    </motion.div>;
}